import type { ReactNode } from 'react'
import {
  Archive,
  ArrowDownLeft,
  ArrowUpRight,
  Banknote,
  CalendarDays,
  Clock,
  Flag,
  Hourglass,
  Layers,
  Lock,
  Mail,
  MessageSquare,
  Paperclip,
  Trash2,
  Zap,
} from 'lucide-react'
import { numeDepartament, type DepartamentSlug } from '@b4l/shared'
import {
  COLOANE,
  coloanaPeTablou,
  CULOARE_DEPARTAMENT,
  FUNDAL_CARD,
  FUNDAL_FINANCIAR,
  PAS_URMATOR,
  sursaScurta,
  type StareTablou,
} from './constante'
import {
  depasit,
  esteAlMeu,
  numeOameni,
  zileDeAzi,
  zileText,
  ziScurt,
  type CardTask,
  type MotivCautare,
  type Persoana,
} from './date'
import { useAuthStore } from '../../stores/auth'

/**
 * Fata cardului. Culoarea apare doar cand cere atentie — termen depasit sau azi, de mult in
 * coloana, urgent, asteapta pe cineva; tot restul (sursa, vechime, comentarii) e gri. Ca la
 * Linear dupa redesign-ul din 2024: nu toate elementele cantaresc la fel.
 */
export function CardFata({
  t,
  cuDepartament,
  trimis,
  laDeschidere,
  laPasUrmator,
  laResponsabil,
  laArhivare,
  laStergere,
  financiar = false,
  motive,
}: {
  t: CardTask
  /** Eticheta de departament, cand pe tablou se vad mai multe deodata. */
  cuDepartament: boolean
  /** Vazut din „Trimise de noi": e la alt departament. */
  trimis: boolean
  laDeschidere: () => void
  laPasUrmator: () => void
  /** Cine are voie: oamenii de pe task (pune, scoate), din avatarele cardului (19.10.2026). */
  laResponsabil?: () => void
  /** Cine are voie să-l mute: scoate task-ul în Arhivă, fără să-l șteargă (16.10.2026). */
  laArhivare?: () => void
  /** Doar la admini: ștergere definitivă. */
  laStergere?: () => void
  /** Legat de bani (vezi `esteFinanciar`): roșu, în orice coloană. */
  financiar?: boolean
  /** La căutare: de ce a ieșit, când nu se vede din titlu. */
  motive?: MotivCautare[]
}) {
  const urgent = t.urgent && t.stare !== 'done'
  const pas = PAS_URMATOR[t.stare]
  const arePas = !!pas && t.actiuni.includes(pas.actiune)
  // Al altcuiva: nici tragere, nici meniu, nici buton. Cine il are scrie pe card.
  const blocat = t.actiuni.length === 0 && t.responsabili.length > 0 && !trimis

  return (
    <article
      tabIndex={0}
      onClick={laDeschidere}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target === e.currentTarget) laDeschidere()
      }}
      aria-label={t.titlu}
      className={`grid cursor-pointer gap-1.5 rounded-caseta border ${financiar ? FUNDAL_FINANCIAR : (FUNDAL_CARD[t.stare as StareTablou] ?? 'border-b4l-border bg-b4l-surface')} px-2.5 pb-2 pt-2.5 text-left transition-shadow hover:border-b4l-blue/60 hover:shadow-ridicat focus-visible:outline focus-visible:outline-2 focus-visible:outline-b4l-blue ${
        urgent ? 'border-l-[3px] border-l-b4l-red pl-2' : ''
      }`}
    >
      {arePas && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            laPasUrmator()
          }}
          className={`absolute right-9 top-1.5 z-10 rounded-md border border-b4l-border bg-b4l-surface-ridicata px-1.5 py-px text-eticheta font-semibold opacity-0 focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100 ${
            pas.actiune === 'gata'
              ? 'hover:border-b4l-green hover:text-b4l-green-dark'
              : 'hover:border-b4l-blue hover:text-b4l-blue-dark'
          } text-b4l-text`}
        >
          {pas.eticheta}
        </button>
      )}
      {(cuDepartament || trimis) && t.departament && (
        <EtichetaDepartament slug={t.departament} prefix={trimis ? 'la ' : ''} />
      )}
      {financiar && (
        <span className="inline-flex items-center gap-1 justify-self-start text-mic font-semibold text-b4l-red-dark">
          <Banknote size={12} aria-hidden="true" />
          bani · plăți, facturi
        </span>
      )}
      {t.privat && <EtichetaPrivat t={t} />}
      {t.departamentPropus && (
        <span className="inline-flex items-center gap-1 text-mic text-b4l-text-muted">
          <Mail size={12} aria-hidden="true" />
          din email · propus {numeDepartament(t.departamentPropus)}
        </span>
      )}
      <span className="line-clamp-3 pr-6 text-corp font-medium leading-[18px] text-b4l-text">
        {urgent && (
          <Flag size={13} className="mr-1 inline align-[-2px] text-b4l-red" aria-label="Urgent" />
        )}
        {t.titlu}
      </span>
      {motive && motive.length > 0 && <MotiveCautare motive={motive} />}
      {t.grup > 1 && (
        <span className="inline-flex items-center gap-1 text-mic text-b4l-text-muted">
          <Layers size={12} aria-hidden="true" />
          și la încă {t.grup - 1} {t.grup - 1 === 1 ? 'departament' : 'departamente'}
        </span>
      )}
      {t.stare === 'in_asteptare' && t.asteaptaPe && (
        <span className="inline-flex items-center gap-1 justify-self-start rounded-md bg-b4l-surface px-1.5 py-0.5 text-mic text-b4l-purple-dark ring-1 ring-b4l-purple/25">
          <Hourglass size={12} aria-hidden="true" />
          așteaptă <b className="font-semibold">{t.asteaptaPe}</b>
        </span>
      )}
      <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-mic text-b4l-text-dim">
        <Termen t={t} />
        {t.stare !== 'done' && <Vechime t={t} />}
        <Sursa t={t} trimis={trimis} />
        {t.comentarii > 0 && (
          <Meta titlu={`${t.comentarii} comentarii`}>
            <MessageSquare size={12} aria-hidden="true" />
            {t.comentarii}
          </Meta>
        )}
        {t.atasamente > 0 && (
          <Meta titlu={`${t.atasamente} documente`}>
            <Paperclip size={12} aria-hidden="true" />
            {t.atasamente}
          </Meta>
        )}
        <span className="flex-1" />
        {laArhivare && (
          <ButonCard eticheta="Arhivează" pericol={false} onClick={laArhivare}>
            <Archive size={13} aria-hidden="true" />
          </ButonCard>
        )}
        {laStergere && (
          <ButonCard eticheta="Șterge definitiv (doar admin)" pericol onClick={laStergere}>
            <Trash2 size={13} aria-hidden="true" />
          </ButonCard>
        )}
        {blocat && (
          <Lock
            size={11}
            className="text-b4l-text-dim"
            aria-label="Îl mută doar cine l-a preluat"
          />
        )}
        {laResponsabil && t.stare !== 'done' ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              laResponsabil()
            }}
            onKeyDown={(e) => e.stopPropagation()}
            className="rounded-full outline-offset-2 hover:ring-2 hover:ring-b4l-blue/50"
            aria-label={
              t.responsabili.length
                ? `Colegii de pe task: ${numeOameni(t.responsabili)}. Schimbă`
                : 'Pune pe cineva pe task'
            }
            title={
              t.responsabili.length
                ? `${numeOameni(t.responsabili)} — schimbă`
                : 'Pune pe cineva pe task'
            }
          >
            <Avatare oameni={t.responsabili} />
          </button>
        ) : t.responsabili.length ? (
          <Avatare oameni={t.responsabili} />
        ) : (
          t.stare === 'de_preluat' && <Avatar nume={null} />
        )}
      </span>
    </article>
  )
}

function Meta({
  titlu,
  ton,
  children,
}: {
  titlu?: string
  ton?: 'rosu' | 'galben' | 'verde'
  children: ReactNode
}) {
  const culoare =
    ton === 'rosu'
      ? 'font-semibold text-b4l-red-dark'
      : ton === 'galben'
        ? 'font-semibold text-b4l-amber-dark'
        : ton === 'verde'
          ? 'text-b4l-green-dark'
          : ''
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap ${culoare}`} title={titlu}>
      {children}
    </span>
  )
}

/** Termenul: gri cat e departe; culoare doar cand cere atentie (azi, maine, depasit). */
function Termen({ t }: { t: CardTask }) {
  if (t.stare === 'done' && t.inchis)
    return (
      <Meta ton="verde">
        <CalendarDays size={12} aria-hidden="true" />
        terminat {ziScurt(t.inchis)}
      </Meta>
    )
  if (!t.termen) return null
  const d = zileDeAzi(t.termen)
  if (depasit(t))
    return (
      <Meta ton="rosu" titlu={`Termen ${ziScurt(t.termen)}`}>
        <CalendarDays size={12} aria-hidden="true" />
        depășit {-d} {-d === 1 ? 'zi' : 'zile'}
      </Meta>
    )
  if (d <= 1)
    return (
      <Meta ton="galben" titlu={`Termen ${ziScurt(t.termen)}`}>
        <CalendarDays size={12} aria-hidden="true" />
        {d === 0 ? 'azi' : 'mâine'}
      </Meta>
    )
  return (
    <Meta titlu="Termen">
      <CalendarDays size={12} aria-hidden="true" />
      {ziScurt(t.termen)}
    </Meta>
  )
}

/** De cand sta cardul in coloana lui; galben cand sta prea mult (munca blocata iese singura). */
function Vechime({ t }: { t: CardTask }) {
  const zile = -zileDeAzi(t.inColoanaDin)
  // Cele vechi, ramase `todo`, stau in „În lucru" — si se ingalbenesc ca ele.
  const col = COLOANE.find((c) => c.id === coloanaPeTablou(t.stare))
  const veche = !!col && zile >= col.vechiDupa
  return (
    <Meta
      ton={veche ? 'galben' : undefined}
      titlu={`De cât timp e în „${col?.nume ?? ''}”${veche ? ' — de mult' : ''}`}
    >
      <Clock size={12} aria-hidden="true" />
      {zileText(zile)}
    </Meta>
  )
}

function Sursa({ t, trimis }: { t: CardTask; trimis: boolean }) {
  if (t.sursa && !trimis)
    return (
      <Meta titlu={`Cerere de la ${numeDepartament(t.sursa)}`}>
        <ArrowDownLeft size={12} aria-hidden="true" />
        {numeDepartament(t.sursa)}
      </Meta>
    )
  if (trimis) return null
  const s = sursaScurta(t)
  if (!s) return null
  return (
    <Meta>
      {s === 'email' ? <Mail size={12} aria-hidden="true" /> : <Zap size={12} aria-hidden="true" />}
      {s}
    </Meta>
  )
}

const ETICHETA_MOTIV: Record<MotivCautare['camp'], string> = {
  numar: 'numărul',
  responsabil: 'responsabil:',
  creat: 'creat de:',
  client: 'client:',
  detalii: 'în detalii:',
  comentariu: 'în comentarii:',
}

/** „în detalii: …semnează PV-ul…", cu cuvântul căutat evidențiat. Pe card și în Arhivă. */
export function MotiveCautare({ motive }: { motive: MotivCautare[] }) {
  return (
    <span className="grid gap-0.5">
      {motive.map((m, i) => {
        const bucati: ReactNode[] = []
        let la = 0
        for (const [a, b] of m.evidentiere) {
          if (a < la) continue
          bucati.push(m.text.slice(la, a))
          bucati.push(
            <mark key={a} className="rounded-sm bg-b4l-amber-light px-px text-b4l-text">
              {m.text.slice(a, b)}
            </mark>,
          )
          la = b
        }
        bucati.push(m.text.slice(la))
        return (
          <span key={i} className="line-clamp-2 break-words text-mic text-b4l-text-muted">
            <span className="font-semibold text-b4l-text-dim">{ETICHETA_MOTIV[m.camp]}</span>{' '}
            {bucati}
          </span>
        )
      })}
    </span>
  )
}

export function EtichetaDepartament({
  slug,
  prefix = '',
}: {
  slug: DepartamentSlug
  prefix?: string
}) {
  return (
    <span className="inline-flex items-center gap-1.5 text-eticheta font-semibold text-b4l-text-muted">
      {prefix === 'la ' ? (
        <ArrowUpRight size={12} aria-hidden="true" />
      ) : (
        <span
          className={`h-1.5 w-1.5 rounded-full ${CULOARE_DEPARTAMENT[slug].bulina}`}
          aria-hidden="true"
        />
      )}
      {prefix}
      {numeDepartament(slug)}
    </span>
  )
}

/** Initialele omului. Cercul punctat = inca al nimanui („De preluat"). */
export function Avatar({ nume, mic = true }: { nume: string | null; mic?: boolean }) {
  const marime = mic ? 'h-5 w-5 text-[9px]' : 'h-6 w-6 text-[10px]'
  if (!nume)
    return (
      <span
        className={`inline-grid shrink-0 place-items-center rounded-full border-[1.5px] border-dashed border-b4l-text-dim ${marime}`}
        title="Încă al nimănui — îl preia cine are timp"
        aria-label="Nepreluat"
      />
    )
  const init = nume
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
  return (
    <span
      className={`inline-grid shrink-0 place-items-center rounded-full bg-b4l-blue-light font-bold text-b4l-blue-dark ${marime}`}
      title={`Preluat de ${nume}`}
      aria-label={`Preluat de ${nume}`}
    >
      {init}
    </span>
  )
}

/**
 * Oamenii de pe un task, suprapusi (19.10.2026: pot fi mai multi). Pana la trei avatare, apoi
 * „+N" — un card ingust nu tine mai multe, iar numele intregi stau in panou.
 */
export function Avatare({ oameni, mic = true }: { oameni: Persoana[]; mic?: boolean }) {
  if (oameni.length === 0) return <Avatar nume={null} mic={mic} />
  const vazuti = oameni.slice(0, 3)
  const rest = oameni.length - vazuti.length
  const marime = mic ? 'h-5 min-w-5 text-[9px]' : 'h-6 min-w-6 text-[10px]'
  return (
    <span className="inline-flex items-center" title={`Preluat de ${numeOameni(oameni)}`}>
      {vazuti.map((o, i) => (
        <span
          key={o.id}
          className={`rounded-full ring-2 ring-b4l-surface ${i > 0 ? '-ml-1.5' : ''}`}
        >
          <Avatar nume={o.name} mic={mic} />
        </span>
      ))}
      {rest > 0 && (
        <span
          className={`-ml-1.5 inline-grid place-items-center rounded-full bg-b4l-bg-tare px-1 font-bold text-b4l-text-muted ring-2 ring-b4l-surface ${marime}`}
          aria-label={`și încă ${rest}`}
        >
          +{rest}
        </span>
      )}
    </span>
  )
}

/** Buton mic pe fața cardului. Oprește clicul: altfel s-ar deschide și panoul task-ului. */
function ButonCard({
  eticheta,
  pericol,
  onClick,
  children,
}: {
  eticheta: string
  pericol: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      onKeyDown={(e) => e.stopPropagation()}
      aria-label={eticheta}
      title={eticheta}
      className={`rounded-md p-1 text-b4l-text-dim outline-offset-2 ${
        pericol
          ? 'hover:bg-b4l-red-light hover:text-b4l-red-dark'
          : 'hover:bg-b4l-bg hover:text-b4l-text'
      }`}
    >
      {children}
    </button>
  )
}

/**
 * Lacătul unui task privat. Cine e pe el vede de la cine e; cine l-a dat, la cine e — fiecare
 * află ce îl privește, iar cuvântul „Privat” spune că restul firmei nu-l vede (din 19.10.2026
 * nici adminii, dacă nu sunt pe el — doar cine urmărește privatele).
 */
function EtichetaPrivat({ t }: { t: CardTask }) {
  const eu = useAuthStore((s) => s.user?.id)
  const altii = t.responsabili.filter((r) => r.id !== eu)
  const cine = esteAlMeu(t, eu)
    ? t.privatDe && t.privatDe.id !== eu
      ? `de la ${t.privatDe.name}`
      : altii.length
        ? `cu ${numeOameni(altii)}`
        : null
    : numeOameni(t.responsabili) || null
  return (
    <span
      className="inline-flex items-center gap-1 justify-self-start rounded-full bg-b4l-purple-light px-2 py-px text-eticheta font-semibold text-b4l-purple-dark"
      title="Privat: îl văd doar cei de pe el, cine l-a dat și cine urmărește privatele"
    >
      <Lock size={11} aria-hidden="true" />
      Privat{cine ? ` · ${cine}` : ''}
    </span>
  )
}
